/**
 * AccessibilityContext.js
 *
 * Centralny kontekst React do zarządzania preferencjami dostępności.
 * Przechowuje i persystuje (localStorage) ustawienia:
 *  - rozmiar czcionki (normal / large / largest)
 *  - szerokie odstępy liter
 *
 * Używa data-atrybutów na <html>, dzięki czemu CSS może reagować
 * bez konieczności przepisywania klas przez JS na każdym komponencie.
 *
 * Zgodność: React 17+, Gatsby SSR (flaga `ready` zapobiega hydration mismatch).
 */

import React, { createContext, useContext, useState, useEffect } from "react"

const AccessibilityContext = createContext(null)

export const FONT_SIZE_LEVELS = ["normal", "large", "largest"]

const STORAGE_KEYS = {
  size:    "a11y-size",
  spacing: "a11y-spacing",
}

function safeGet(key) {
  try { return localStorage.getItem(key) } catch (_) { return null }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, value) } catch (_) {}
}

export function AccessibilityProvider({ children }) {
  const [fontSizeLevel, setFontSizeLevel] = useState("normal")
  const [letterSpacing, setLetterSpacing] = useState(false)
  // Flaga SSR: nie dotykamy DOM przed mount (Gatsby renderuje po serwerze)
  const [ready, setReady] = useState(false)

  // ── Odczyt preferencji po mount ──────────────────────────────────────
  useEffect(() => {
    const s = FONT_SIZE_LEVELS.includes(safeGet(STORAGE_KEYS.size))
      ? safeGet(STORAGE_KEYS.size)
      : "normal"
    const l = safeGet(STORAGE_KEYS.spacing) === "true"
    setFontSizeLevel(s)
    setLetterSpacing(l)
    setReady(true)
  }, [])

  // ── Zastosowanie preferencji jako data-atrybuty na <html> ────────────
  useEffect(() => {
    if (!ready) return
    const html = document.documentElement
    html.dataset.a11ySize    = fontSizeLevel
    html.dataset.a11ySpacing = letterSpacing ? "wide" : "normal"
    safeSet(STORAGE_KEYS.size,    fontSizeLevel)
    safeSet(STORAGE_KEYS.spacing, letterSpacing)
  }, [fontSizeLevel, letterSpacing, ready])

  // ── Akcje ────────────────────────────────────────────────────────────
  const increaseFontSize = () =>
    setFontSizeLevel(p => {
      const i = FONT_SIZE_LEVELS.indexOf(p)
      return FONT_SIZE_LEVELS[Math.min(i + 1, FONT_SIZE_LEVELS.length - 1)]
    })

  const decreaseFontSize = () =>
    setFontSizeLevel(p => {
      const i = FONT_SIZE_LEVELS.indexOf(p)
      return FONT_SIZE_LEVELS[Math.max(i - 1, 0)]
    })

  const resetFontSize = () => setFontSizeLevel("normal")

  const toggleLetterSpacing = () => setLetterSpacing(p => !p)

  return (
    <AccessibilityContext.Provider
      value={{
        fontSizeLevel,
        letterSpacing,
        increaseFontSize,
        decreaseFontSize,
        resetFontSize,
        toggleLetterSpacing,
        FONT_SIZE_LEVELS,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  )
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext)
  if (!ctx)
    throw new Error("useAccessibility musi być używany wewnątrz AccessibilityProvider")
  return ctx
}
